/**
 * Parnes HaYom — Source-Faithful Parent Routes
 *
 * Replaces the owned placeholder at /parent/portal/parnes-hayom with a real
 * internal-owned parent flow: halachic calendar, Stripe Connect checkout,
 * success/receipt, and donation history.
 *
 * RULE: Do not add root `px-*` or `py-*` classes on <main> elements (shell owns insets per CLAUDE.md).
 *
 * Locked Decisions (implementation-plan.md):
 *   #1 Canonical slug = parnes-hayom
 *   #2 Authenticated under /parent/portal/parnes-hayom
 *   #3 Payments stay in domainPaymentsApi via createHostedPaymentSession
 *   #4 PaymentModuleId = 'donations' — campaign metadata differentiates
 *   #6 Moderation defaults to auto_approve in first launch
 *
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/implementation-plan.md
 */

import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  Clock,
  Heart,
  History,
  Sun,
  SunDim,
} from "lucide-react";
import {
  PaymentReturnNotice,
  readHostedCheckoutReturnState,
} from "@triedge/payments";
import { useCallback, useMemo, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  useGetDonationsCampaignCalendarQuery,
  useGetSettingsModuleConfigQuery,
  useGetDonationsTenantConfigQuery,
  useListDonationsFamilyHistoryQuery,
} from "../../../state/domainRouterApi";
import { parseSettingsState } from "../../../settings/settingsState";
import { resolveCognitoProfile } from "../../../utils/cognito-profile";
import { createHostedPaymentSession } from "../../../utils/payments";
import {
  HalachicCalendarGrid,
  useHalachicDate,
  useSunsetBoundary,
} from "../../shared/halachic-calendar";
import {
  ModuleSectionNav,
  type ModuleSectionNavItem,
} from "../../../components/navigation/ModuleSectionNav";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import type {
  GeoConfig,
  HostedPaymentSessionInput,
} from "@triedge/domain-core";

// ---------------------------------------------------------------------------
// Shared Utilities (reused from ParentDonationsRoutes pattern)
// ---------------------------------------------------------------------------

const SETTINGS_MODULE_KEY = "staff_settings";

type SponsorshipTier = "one_class" | "one_division" | "schoolwide";

type DivisionOption = {
  id: string;
  type: string;
  displayName: string;
  enabled: boolean;
};

type ParnesHayomPricing = {
  oneClassAmount: number;
  oneDivisionAmount: number;
  schoolwideAmount: number;
};

type ParnesHayomRouteState = {
  date?: string | null;
  tier?: SponsorshipTier | null;
  divisionId?: string | null;
  checkout?: "success" | "cancel" | null;
};

const DEFAULT_PARNES_HAYOM_PRICING: ParnesHayomPricing = {
  oneClassAmount: 180,
  oneDivisionAmount: 500,
  schoolwideAmount: 1000,
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const parseGregorianDate = (value: string | null | undefined): Date | null => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toWholeDollarAmount = (value: unknown, fallback: number): number => {
  const parsed = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const resolveSponsorshipTier = (
  value: string | null | undefined,
): SponsorshipTier => {
  switch (value) {
    case "one_division":
      return "one_division";
    case "schoolwide":
      return "schoolwide";
    case "one_class":
    default:
      return "one_class";
  }
};

const resolveEnabledDivisionOptions = (value: unknown): DivisionOption[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => asRecord(entry))
    .map((entry) => ({
      id: String(entry.id ?? "").trim(),
      type: String(entry.type ?? "").trim(),
      displayName: String(entry.displayName ?? "").trim(),
      enabled: entry.enabled !== false,
    }))
    .filter((entry) => entry.id && entry.displayName && entry.enabled);
};

const resolveParnesHayomPricing = (value: unknown): ParnesHayomPricing => {
  const organization = asRecord(asRecord(value).organization);
  const ext = asRecord(organization.ext);
  const pricing = asRecord(ext.parnesHayomPricing);
  return {
    oneClassAmount: toWholeDollarAmount(
      pricing.oneClassAmount,
      DEFAULT_PARNES_HAYOM_PRICING.oneClassAmount,
    ),
    oneDivisionAmount: toWholeDollarAmount(
      pricing.oneDivisionAmount,
      DEFAULT_PARNES_HAYOM_PRICING.oneDivisionAmount,
    ),
    schoolwideAmount: toWholeDollarAmount(
      pricing.schoolwideAmount,
      DEFAULT_PARNES_HAYOM_PRICING.schoolwideAmount,
    ),
  };
};

const formatDollarAmount = (amount: number): string =>
  `$${amount.toLocaleString("en-US")}`;

const formatCurrencyCents = (amountCents: number): string =>
  `$${(amountCents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const buildParnesHayomSearchParams = (
  state: ParnesHayomRouteState,
): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.date) params.set("date", state.date);
  if (state.tier) params.set("tier", state.tier);
  if (state.divisionId) params.set("divisionId", state.divisionId);
  if (state.checkout) params.set("checkout", state.checkout);
  return params;
};

const buildParnesHayomRoute = (
  pathname: string,
  state: ParnesHayomRouteState,
): string => {
  const params = buildParnesHayomSearchParams(state);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

const resolveCheckoutUrl = (
  kind: "success" | "cancel",
  state: Pick<ParnesHayomRouteState, "date" | "tier" | "divisionId">,
): string => {
  const url = new URL(window.location.href);
  url.pathname =
    kind === "success"
      ? "/parent/portal/parnes-hayom/success"
      : "/parent/portal/parnes-hayom/checkout";
  url.search = buildParnesHayomSearchParams({
    ...state,
    checkout: kind,
  }).toString();
  return url.toString();
};

const resolvePaymentProfile = () => resolveCognitoProfile();

const extractPaymentErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim())
    return error.message.trim();
  if (error && typeof error === "object") {
    const msg =
      "message" in error
        ? String((error as { message?: unknown }).message ?? "").trim()
        : "";
    if (msg) return msg;
    if (
      Array.isArray((error as { errors?: Array<{ message?: unknown }> }).errors)
    ) {
      const nested = (error as { errors?: Array<{ message?: unknown }> }).errors
        ?.map((e) => String(e?.message ?? "").trim())
        .filter(Boolean)
        .join("; ");
      if (nested) return nested;
    }
  }
  return String(error ?? "").trim() || "Unknown payment error";
};

const sanitizePhoneInput = (value: string): string => value.replace(/\D/g, "");

const resolveSponsorshipAmountCents = (
  tier: SponsorshipTier,
  pricing: ParnesHayomPricing,
): number => {
  switch (tier) {
    case "one_division":
      return pricing.oneDivisionAmount * 100;
    case "schoolwide":
      return pricing.schoolwideAmount * 100;
    case "one_class":
    default:
      return pricing.oneClassAmount * 100;
  }
};

const resolveSponsorshipLabel = (
  tier: SponsorshipTier | null | undefined,
  divisionLabel?: string | null,
): string => {
  switch (tier) {
    case "one_division":
      return divisionLabel ? `One division - ${divisionLabel}` : "One division";
    case "schoolwide":
      return "Schoolwide";
    case "one_class":
    default:
      return "One class";
  }
};

const resolveHistorySponsorshipLabel = (
  item: Record<string, unknown>,
): string => {
  const campaignType = String(item.campaignType ?? "").trim();
  const tier = resolveSponsorshipTier(String(item.sponsorshipTier ?? ""));
  const divisionLabel = String(item.sponsorshipDivisionLabel ?? "").trim();
  if (String(item.sponsorshipTier ?? "").trim()) {
    return resolveSponsorshipLabel(tier, divisionLabel || null);
  }
  return campaignType === "parnes-hayom" ? "One class" : "\u2014";
};

const resolveSponsorshipLineItem = (
  tier: SponsorshipTier,
  divisionLabel: string | null,
  dateLabel: string,
): HostedPaymentSessionInput["lineItems"][number] => {
  const name =
    tier === "schoolwide"
      ? "Parnes HaYom - Schoolwide"
      : tier === "one_division"
        ? "Parnes HaYom - One Division"
        : "Parnes HaYom - One Class";
  const descriptionParts = [
    dateLabel.trim(),
    tier === "one_division" ? (divisionLabel ?? "") : "",
  ].filter(Boolean);
  return {
    name,
    description: descriptionParts.join(" - "),
    quantity: 1,
    unitAmountCents: 0,
  };
};

const SPONSORSHIP_OPTIONS: Array<{
  tier: SponsorshipTier;
  label: string;
  description: string;
}> = [
  {
    tier: "one_class",
    label: "One class",
    description: "Dedicate a single class for the selected day.",
  },
  {
    tier: "one_division",
    label: "One division",
    description: "Dedicate all learning in one school division.",
  },
  {
    tier: "schoolwide",
    label: "Schoolwide",
    description: "Dedicate learning across the entire school.",
  },
];

function SponsorshipTierSelector({
  tier,
  pricing,
  divisions,
  divisionId,
  onTierChange,
  onDivisionChange,
  title = "Sponsorship Options",
  description,
}: {
  tier: SponsorshipTier;
  pricing: ParnesHayomPricing;
  divisions: DivisionOption[];
  divisionId: string;
  onTierChange: (tier: SponsorshipTier) => void;
  onDivisionChange: (divisionId: string) => void;
  title?: string;
  description?: string;
}) {
  const showDivisionPicker = tier === "one_division";

  return (
    <div className="yst-surface-panel p-6">
      <div>
        <h2 className="font-heading text-lg font-semibold text-irongrey">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-irongrey/70">{description}</p>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {SPONSORSHIP_OPTIONS.map((option) => {
          const amount =
            option.tier === "one_division"
              ? pricing.oneDivisionAmount
              : option.tier === "schoolwide"
                ? pricing.schoolwideAmount
                : pricing.oneClassAmount;
          const selected = option.tier === tier;

          return (
            <label
              key={option.tier}
              data-testid={`parent-parnes-hayom-tier-${option.tier}`}
              onClick={() => onTierChange(option.tier)}
              className={`yst-motion-color flex cursor-pointer flex-col rounded-2xl border p-4 transition ${
                selected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-white hover:border-primary/40 hover:bg-primary/5"
              }`}
            >
              <input
                type="radio"
                name="parnes-hayom-sponsorship-tier"
                value={option.tier}
                checked={selected}
                onChange={() => onTierChange(option.tier)}
                data-testid={`parent-parnes-hayom-tier-radio-${option.tier}`}
                className="sr-only"
              />
              <span className="pointer-events-none text-xs font-semibold uppercase tracking-[0.08em] text-primary">
                {formatDollarAmount(amount)}
              </span>
              <span className="pointer-events-none mt-2 text-base font-semibold text-irongrey">
                {option.label}
              </span>
              <span className="pointer-events-none mt-1 text-sm text-irongrey/70">
                {option.description}
              </span>
            </label>
          );
        })}
      </div>

      {showDivisionPicker ? (
        <div className="mt-4 space-y-2">
          <label
            htmlFor="parnes-hayom-division-select"
            className="block text-sm font-medium text-irongrey"
          >
            Division<span className="ml-1 text-red-500">*</span>
          </label>
          <select
            id="parnes-hayom-division-select"
            value={divisionId}
            onChange={(event) => onDivisionChange(event.target.value)}
            className="yst-control-input block w-full px-3 py-2.5 text-sm"
          >
            <option value="">Select a division</option>
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.displayName}
              </option>
            ))}
          </select>
          {divisions.length === 0 ? (
            <p className="text-xs text-irongrey/60">
              Enable at least one division in school settings to offer the
              one-division sponsorship.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab Navigation
// ---------------------------------------------------------------------------

const TABS: ModuleSectionNavItem[] = [
  {
    id: "calendar",
    label: "Calendar",
    to: "/parent/portal/parnes-hayom",
    end: true,
    icon: CalendarDays,
  },
  {
    id: "history",
    label: "History",
    to: "/parent/portal/parnes-hayom/history",
    icon: History,
  },
];

function ParnesHayomTabs({ activeTab }: { activeTab: "calendar" | "history" }) {
  return (
    <ModuleSectionNav
      items={TABS}
      activeId={activeTab}
      ariaLabel="Parnes HaYom sections"
      desktopClassName="source-faithful-tabs"
    />
  );
}

// ---------------------------------------------------------------------------
// Default Geo (Lakewood, NJ) — used when tenant config is not yet loaded
// ---------------------------------------------------------------------------

const DEFAULT_GEO: GeoConfig = {
  latitude: 40.096,
  longitude: -74.2177,
  timezone: "America/New_York",
  elevationMeters: 12,
};

const PRIMARY = "var(--primary)";
const TEXT = "var(--iron-grey)";
const BORDER = "var(--border)";

// ---------------------------------------------------------------------------
// 1. Campaign Landing Page
// ---------------------------------------------------------------------------

function ParnesHayomCampaignLandingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get("date");
  const selectedTier = resolveSponsorshipTier(searchParams.get("tier"));
  const selectedDivisionId = searchParams.get("divisionId") ?? "";
  const initialMonthDate = parseGregorianDate(selectedDate);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = initialMonthDate ?? new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  const {
    data: calendarData,
    isLoading: calendarLoading,
    error: calendarError,
  } = useGetDonationsCampaignCalendarQuery({
    campaignType: "parnes-hayom",
    year: currentMonth.year,
    month: currentMonth.month,
  });

  const { data: tenantConfig } = useGetDonationsTenantConfigQuery();
  const settingsQuery = useGetSettingsModuleConfigQuery({
    moduleKey: SETTINGS_MODULE_KEY,
  });

  const settingsState = useMemo(() => {
    const payload = asRecord(settingsQuery.data).payload;
    return parseSettingsState(payload);
  }, [settingsQuery.data]);

  const pricing = useMemo(
    () => resolveParnesHayomPricing(settingsState),
    [settingsState],
  );
  const divisions = useMemo(
    () => resolveEnabledDivisionOptions(settingsState.divisions),
    [settingsState.divisions],
  );
  const selectedDivision =
    divisions.find((division) => division.id === selectedDivisionId) ?? null;
  const selectedSponsorshipAmountCents = resolveSponsorshipAmountCents(
    selectedTier,
    pricing,
  );
  const selectedSponsorshipLabel = resolveSponsorshipLabel(
    selectedTier,
    selectedDivision?.displayName ?? null,
  );

  const geoConfig = tenantConfig?.geo ?? DEFAULT_GEO;
  const { isSunsetApplied, toggleSunset } = useSunsetBoundary(
    selectedDate,
    geoConfig,
  );
  const hebrewDate = useHalachicDate(selectedDate, isSunsetApplied, geoConfig);

  const handleMonthChange = useCallback((direction: -1 | 1) => {
    setCurrentMonth((prev) => {
      let nextMonth = prev.month + direction;
      let nextYear = prev.year;
      if (nextMonth < 1) {
        nextMonth = 12;
        nextYear--;
      }
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear++;
      }
      return { year: nextYear, month: nextMonth };
    });
  }, []);

  const updateLandingRoute = useCallback(
    (nextState: Partial<ParnesHayomRouteState>) => {
      const params = buildParnesHayomSearchParams({
        date: nextState.date !== undefined ? nextState.date : selectedDate,
        tier: nextState.tier !== undefined ? nextState.tier : selectedTier,
        divisionId:
          nextState.divisionId !== undefined
            ? nextState.divisionId
            : selectedDivisionId,
      });
      setSearchParams(params, { replace: true });
    },
    [selectedDate, selectedDivisionId, selectedTier, setSearchParams],
  );

  const handleDateSelect = useCallback(
    (date: string | null) => {
      updateLandingRoute({ date, tier: selectedTier });
    },
    [selectedTier, updateLandingRoute],
  );

  const handleTierChange = useCallback(
    (tier: SponsorshipTier) => {
      updateLandingRoute({
        tier,
        divisionId: tier === "one_division" ? selectedDivisionId : null,
      });
    },
    [selectedDivisionId, updateLandingRoute],
  );

  const handleDivisionChange = useCallback(
    (divisionId: string) => {
      updateLandingRoute({ divisionId });
    },
    [updateLandingRoute],
  );

  const handleContinueToCheckout = useCallback(() => {
    if (!selectedDate) return;
    if (selectedTier === "one_division" && !selectedDivision) return;
    navigate(
      buildParnesHayomRoute("/parent/portal/parnes-hayom/checkout", {
        date: selectedDate,
        tier: selectedTier,
        divisionId:
          selectedTier === "one_division"
            ? (selectedDivision?.id ?? null)
            : null,
      }),
    );
  }, [navigate, selectedDate, selectedDivision, selectedTier]);

  return (
    <main
      data-testid="parent-parnes-hayom-landing"
      data-extracted-from="source:parent-parnes-hayom-v1.1"
      data-route-ready="true"
    >
      <YstRouteTransition className="space-y-6">
        {/* Header */}
        <div>
          <p className="yst-surface-eyebrow">Parnes HaYom</p>
          <h1 className="yst-surface-title mt-1 md:text-3xl">
            Sponsor a Day of Learning
          </h1>
          <p className="yst-surface-subtitle mt-2">
            Select a date to dedicate a day of learning in honor or memory of a
            loved one.
          </p>
        </div>

        {/* Tab Navigation */}
        <ParnesHayomTabs activeTab="calendar" />

        {/* Campaign Info Banner */}
        {calendarData ? (
          <div className="yst-surface-panel flex flex-wrap items-center gap-4 bg-primary/5 p-4">
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-primary" aria-hidden="true" />
              <span className="text-sm font-medium text-irongrey">
                {calendarData.campaignName}
              </span>
            </div>
            <span className="text-xs text-irongrey/60">
              {calendarData.startDate} &mdash; {calendarData.endDate}
            </span>
          </div>
        ) : null}

        {/* Error State */}
        {calendarError ? (
          <SupportErrorMessage className="p-4">
            Calendar data is temporarily unavailable.
          </SupportErrorMessage>
        ) : null}

        <SponsorshipTierSelector
          tier={selectedTier}
          pricing={pricing}
          divisions={divisions}
          divisionId={selectedDivisionId}
          onTierChange={handleTierChange}
          onDivisionChange={handleDivisionChange}
          description="Choose the dedication level that matches the current school sponsorship options."
        />

        {/* Calendar Grid */}
        <HalachicCalendarGrid
          calendarData={calendarLoading ? null : (calendarData?.grid ?? null)}
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          onMonthChange={handleMonthChange}
          testId="parnes-hayom-calendar"
        />

        {/* Selected Date Panel */}
        {selectedDate ? (
          <div className="yst-surface-panel border-primary/20 p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-irongrey/50">
                  Selected Date
                </p>
                <p className="mt-1 text-lg font-semibold text-irongrey">
                  {selectedDate}
                </p>
                {hebrewDate ? (
                  <p
                    className="mt-0.5 text-sm font-medium text-primary"
                    dir="rtl"
                  >
                    {hebrewDate.hebrewDateLabel}
                  </p>
                ) : null}
                {hebrewDate ? (
                  <p className="mt-0.5 text-xs text-irongrey/60">
                    {hebrewDate.hebrewDateEnglish}
                  </p>
                ) : null}

                <div className="mt-3 space-y-0.5">
                  <p className="text-sm font-medium text-irongrey">
                    {selectedSponsorshipLabel}
                  </p>
                  <p className="text-xs text-irongrey/60">
                    {formatCurrencyCents(selectedSponsorshipAmountCents)}
                  </p>
                </div>

                {/* Sunset Toggle */}
                <button
                  type="button"
                  onClick={toggleSunset}
                  className="yst-motion-color mt-2 flex items-center gap-1.5 text-xs text-irongrey/60 hover:text-primary"
                  aria-pressed={isSunsetApplied}
                >
                  {isSunsetApplied ? (
                    <SunDim className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <Sun className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {isSunsetApplied
                    ? "After sunset (next halachic day)"
                    : "Before sunset"}
                </button>
              </div>

              <button
                type="button"
                onClick={handleContinueToCheckout}
                disabled={selectedTier === "one_division" && !selectedDivision}
                className="yst-button-primary flex items-center gap-2 px-6 py-3 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              >
                Continue to Checkout
                <CalendarDays className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </YstRouteTransition>
    </main>
  );
}

// ---------------------------------------------------------------------------
// 2. Checkout Page
// ---------------------------------------------------------------------------

function ParnesHayomCheckoutPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const selectedTier = resolveSponsorshipTier(searchParams.get("tier"));
  const selectedDivisionId = searchParams.get("divisionId") ?? "";
  const hostedReturnState = readHostedCheckoutReturnState(searchParams);
  const parsedDate = useMemo(() => parseGregorianDate(dateParam), [dateParam]);

  const { data: tenantConfig } = useGetDonationsTenantConfigQuery();
  const settingsQuery = useGetSettingsModuleConfigQuery({
    moduleKey: SETTINGS_MODULE_KEY,
  });
  const { data: calendarData } = useGetDonationsCampaignCalendarQuery(
    parsedDate
      ? {
          campaignType: "parnes-hayom",
          year: parsedDate.getFullYear(),
          month: parsedDate.getMonth() + 1,
        }
      : undefined,
  );

  const settingsState = useMemo(() => {
    const payload = asRecord(settingsQuery.data).payload;
    return parseSettingsState(payload);
  }, [settingsQuery.data]);
  const pricing = useMemo(
    () => resolveParnesHayomPricing(settingsState),
    [settingsState],
  );
  const divisions = useMemo(
    () => resolveEnabledDivisionOptions(settingsState.divisions),
    [settingsState.divisions],
  );
  const selectedDivision =
    divisions.find((division) => division.id === selectedDivisionId) ?? null;
  const selectedDivisionLabel = selectedDivision?.displayName ?? null;
  const sponsorshipAmountCents = resolveSponsorshipAmountCents(
    selectedTier,
    pricing,
  );
  const sponsorshipLabel = resolveSponsorshipLabel(
    selectedTier,
    selectedDivisionLabel,
  );

  const geoConfig = tenantConfig?.geo ?? DEFAULT_GEO;
  const { isSunsetApplied, toggleSunset } = useSunsetBoundary(
    dateParam,
    geoConfig,
  );
  const hebrewDate = useHalachicDate(dateParam, isSunsetApplied, geoConfig);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const profile = useMemo(() => resolvePaymentProfile(), []);

  const [formData, setFormData] = useState({
    dedicationType: "" as "" | "HONOR" | "MEMORY",
    honoreeName: "",
    occasion: "",
    message: "",
    firstName: "",
    lastName: "",
    email: profile?.email ?? "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    isAnonymous: false,
    publicDisplayName: "",
    coverFeesOptIn: false,
  });

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = event.target;
    const sanitizedValue = name === "phone" ? sanitizePhoneInput(value) : value;
    const checked =
      type === "checkbox"
        ? (event.target as HTMLInputElement).checked
        : undefined;
    setFormData((prev) => ({
      ...prev,
      [name]: checked !== undefined ? checked : sanitizedValue,
    }));
  };

  const updateCheckoutRoute = useCallback(
    (nextState: Partial<ParnesHayomRouteState>) => {
      const nextParams = buildParnesHayomSearchParams({
        date: nextState.date !== undefined ? nextState.date : dateParam,
        tier: nextState.tier !== undefined ? nextState.tier : selectedTier,
        divisionId:
          nextState.divisionId !== undefined
            ? nextState.divisionId
            : selectedDivisionId,
        checkout: hostedReturnState === "cancel" ? "cancel" : null,
      });
      setSearchParams(nextParams, { replace: true });
    },
    [
      dateParam,
      hostedReturnState,
      selectedDivisionId,
      selectedTier,
      setSearchParams,
    ],
  );

  const handleTierChange = useCallback(
    (tier: SponsorshipTier) => {
      setErrorMessage(null);
      updateCheckoutRoute({
        tier,
        divisionId: tier === "one_division" ? selectedDivisionId : null,
      });
    },
    [selectedDivisionId, updateCheckoutRoute],
  );

  const handleDivisionChange = useCallback(
    (divisionId: string) => {
      setErrorMessage(null);
      updateCheckoutRoute({ divisionId });
    },
    [updateCheckoutRoute],
  );

  const feePercent = tenantConfig?.feeRecovery?.defaultPercent ?? 3;
  const feeCents = formData.coverFeesOptIn
    ? Math.round(sponsorshipAmountCents * (feePercent / 100))
    : 0;
  const totalCents = sponsorshipAmountCents + feeCents;
  const hebrewDateLabel = hebrewDate?.hebrewDateLabel ?? "";
  const requiresDivision = selectedTier === "one_division";
  const sponsorshipLineItem = resolveSponsorshipLineItem(
    selectedTier,
    selectedDivisionLabel,
    hebrewDateLabel || dateParam || "Selected date",
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!dateParam || !hebrewDate) return;
    if (requiresDivision && !selectedDivision) {
      setErrorMessage("Choose a division before continuing to checkout.");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);

    try {
      const lineItems: HostedPaymentSessionInput["lineItems"] = [
        {
          ...sponsorshipLineItem,
          quantity: 1,
          unitAmountCents: sponsorshipAmountCents,
        },
      ];

      if (feeCents > 0) {
        lineItems.push({
          name: "Processing Support",
          description: "Optional donor fee recovery",
          quantity: 1,
          unitAmountCents: feeCents,
        });
      }

      const url = await createHostedPaymentSession({
        moduleId: "donations",
        operationKey: "donations_parnes_hayom_checkout",
        mode: "payment",
        currency: "usd",
        successUrl: resolveCheckoutUrl("success", {
          date: dateParam,
          tier: selectedTier,
          divisionId: requiresDivision ? (selectedDivision?.id ?? null) : null,
        }),
        cancelUrl: resolveCheckoutUrl("cancel", {
          date: dateParam,
          tier: selectedTier,
          divisionId: requiresDivision ? (selectedDivision?.id ?? null) : null,
        }),
        customerEmail: formData.email,
        customerReferenceId: profile?.userSub || formData.email,
        description: `Parnes HaYom ${sponsorshipLabel.toLowerCase()} sponsorship`,
        lineItems,
        metadata: {
          route: "/parent/portal/parnes-hayom",
          campaign_type: "parnes-hayom",
          campaign_id: calendarData?.campaignId ?? "",
          target_gregorian_date: dateParam,
          sunset_applied: String(isSunsetApplied),
          hebrew_year: String(hebrewDate.hebrewYear),
          hebrew_month: String(hebrewDate.hebrewMonth),
          hebrew_day: String(hebrewDate.hebrewDay),
          dedication_type: formData.dedicationType,
          honoree_name: formData.honoreeName,
          dedication_message: formData.message,
          donor_first_name: formData.firstName,
          donor_last_name: formData.lastName,
          donor_email: formData.email,
          donor_phone: formData.phone,
          gross_cents: String(sponsorshipAmountCents),
          fee_recovery_cents: String(feeCents),
          sponsorship_tier: selectedTier,
          sponsorship_division_id: requiresDivision
            ? (selectedDivision?.id ?? "")
            : "",
          sponsorship_division_label: requiresDivision
            ? (selectedDivisionLabel ?? "")
            : "",
          address_line1: formData.addressLine1,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postalCode,
          recognition: formData.isAnonymous ? "anonymous" : "public",
          ...(formData.isAnonymous
            ? {}
            : {
                public_display_name:
                  formData.publicDisplayName.trim() ||
                  `${formData.firstName} ${formData.lastName}`.trim(),
              }),
        },
      });
      window.location.assign(url);
    } catch (error) {
      setErrorMessage(extractPaymentErrorMessage(error));
      setSubmitting(false);
    }
  };

  if (!dateParam) {
    return <Navigate to="/parent/portal/parnes-hayom" replace />;
  }

  return (
    <main
      data-testid="parent-parnes-hayom-checkout"
      data-extracted-from="source:parent-parnes-hayom-v1.1"
      data-route-ready="true"
      data-payment-operation-key="donations_parnes_hayom_checkout"
      data-daf-recommendation-scope="not-allowed"
    >
      <YstRouteTransition className="space-y-6">
        <PaymentReturnNotice
          state={hostedReturnState === "cancel" ? "cancel" : null}
          noticeKey="parent-parnes-hayom-checkout"
          successTitle=""
          successBody=""
          cancelTitle="Sponsorship checkout was cancelled"
          cancelBody="No sponsorship was finalized. Your order summary is still here if you want to continue."
          guidanceBody="Your sponsorship details remain on this page until you leave the flow. You can continue checkout again or return to the calendar without losing the selected date."
        />
        {/* Back Navigation */}
        <button
          type="button"
          onClick={() =>
            navigate(
              buildParnesHayomRoute("/parent/portal/parnes-hayom", {
                date: dateParam,
                tier: selectedTier,
                divisionId: requiresDivision
                  ? (selectedDivision?.id ?? null)
                  : null,
              }),
            )
          }
          className="yst-motion-color flex items-center gap-1 text-sm text-irongrey/60 hover:text-primary"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Back to calendar
        </button>

        <div>
          <p className="yst-surface-eyebrow">Parnes HaYom</p>
          <h1 className="yst-surface-title mt-1">Complete Your Sponsorship</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {/* Left Column — Form */}
            <div className="space-y-6">
              {/* Date + Hebrew Date */}
              <div className="yst-surface-panel bg-primary/5 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-irongrey/50">
                      Sponsoring
                    </p>
                    <p className="mt-1 text-lg font-semibold text-irongrey">
                      {dateParam}
                    </p>
                    {hebrewDate ? (
                      <p className="text-sm font-medium text-primary" dir="rtl">
                        {hebrewDate.hebrewDateLabel}
                      </p>
                    ) : null}
                    {hebrewDate ? (
                      <p className="text-xs text-irongrey/60">
                        {hebrewDate.hebrewDateEnglish}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={toggleSunset}
                    className="yst-button-secondary flex items-center gap-1 px-3 py-1.5 text-xs text-irongrey/60 shadow-sm hover:text-primary"
                    aria-pressed={isSunsetApplied}
                  >
                    {isSunsetApplied ? (
                      <SunDim className="h-3.5 w-3.5" />
                    ) : (
                      <Sun className="h-3.5 w-3.5" />
                    )}
                    {isSunsetApplied ? "After sunset" : "Before sunset"}
                  </button>
                </div>
              </div>

              <SponsorshipTierSelector
                tier={selectedTier}
                pricing={pricing}
                divisions={divisions}
                divisionId={selectedDivisionId}
                onTierChange={handleTierChange}
                onDivisionChange={handleDivisionChange}
                title="Sponsorship Level"
                description="Update the sponsorship scope here before you launch Stripe checkout."
              />

              {/* Dedication Section */}
              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-irongrey">
                  Dedication (Optional)
                </legend>
                <div>
                  <label
                    htmlFor="dedicationType"
                    className="mb-1 block text-sm font-medium text-irongrey"
                  >
                    Type
                  </label>
                  <select
                    id="dedicationType"
                    name="dedicationType"
                    value={formData.dedicationType}
                    onChange={handleChange}
                    className="yst-control-input block w-full px-3 py-2.5 text-sm"
                  >
                    <option value="">None</option>
                    <option value="HONOR">In Honor Of</option>
                    <option value="MEMORY">In Memory Of</option>
                  </select>
                </div>
                {formData.dedicationType ? (
                  <>
                    <div>
                      <label
                        htmlFor="honoreeName"
                        className="mb-1 block text-sm font-medium text-irongrey"
                      >
                        Honoree Name<span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        id="honoreeName"
                        name="honoreeName"
                        required
                        value={formData.honoreeName}
                        onChange={handleChange}
                        className="yst-control-input block w-full px-3 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="message"
                        className="mb-1 block text-sm font-medium text-irongrey"
                      >
                        Dedication Message{" "}
                        <span className="font-normal text-irongrey/50">
                          (Optional)
                        </span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={3}
                        value={formData.message}
                        onChange={handleChange}
                        className="yst-control-input block w-full px-3 py-2.5 text-sm"
                      />
                    </div>
                  </>
                ) : null}
              </fieldset>

              {/* Donor Information */}
              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-irongrey">
                  Donor Information
                </legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    { id: "firstName", label: "First Name", required: true },
                    { id: "lastName", label: "Last Name", required: true },
                  ].map((field) => (
                    <div key={field.id}>
                      <label
                        htmlFor={field.id}
                        className="mb-1 block text-sm font-medium text-irongrey"
                      >
                        {field.label}
                        {field.required ? (
                          <span className="ml-1 text-red-500">*</span>
                        ) : null}
                      </label>
                      <input
                        id={field.id}
                        name={field.id}
                        required={field.required}
                        value={formData[field.id as "firstName" | "lastName"]}
                        onChange={handleChange}
                        className="yst-control-input block w-full px-3 py-2.5 text-sm"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-irongrey"
                  >
                    Email<span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="yst-control-input block w-full px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="mb-1 block text-sm font-medium text-irongrey"
                  >
                    Phone{" "}
                    <span className="font-normal text-irongrey/50">
                      (Optional)
                    </span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="yst-control-input block w-full px-3 py-2.5 text-sm"
                  />
                </div>
              </fieldset>

              {/* Recognition */}
              <div className="space-y-3 text-sm">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="isAnonymous"
                    checked={formData.isAnonymous}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="font-medium text-irongrey">
                    Make this donation anonymous
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="coverFeesOptIn"
                    checked={formData.coverFeesOptIn}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="font-medium text-irongrey">
                      Help cover processing costs
                    </span>
                    <p className="mt-0.5 text-xs text-irongrey/50">
                      Optional &mdash; helps cover card processing costs. This
                      amount is itemized separately.
                    </p>
                  </div>
                </label>
              </div>

              {/* Error */}
              {errorMessage ? (
                <SupportErrorMessage className="px-4 py-3 text-sm">
                  {errorMessage}
                </SupportErrorMessage>
              ) : null}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || (requiresDivision && !selectedDivision)}
                className="yst-button-primary flex w-full items-center justify-center gap-2 px-4 py-3 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Heart className="h-5 w-5" aria-hidden="true" />
                {submitting
                  ? "Redirecting to Stripe..."
                  : "Complete Sponsorship"}
              </button>
            </div>

            {/* Right Column — Order Summary */}
            <div className="md:sticky md:top-6">
              <div className="yst-surface-panel p-6">
                <h3 className="font-heading text-lg font-semibold text-irongrey">
                  Order Summary
                </h3>
                <hr className="my-4 border-border" />

                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-irongrey/70">Date</dt>
                    <dd className="font-medium text-irongrey">{dateParam}</dd>
                  </div>
                  {hebrewDate ? (
                    <div className="flex justify-between">
                      <dt className="text-irongrey/70">Hebrew Date</dt>
                      <dd className="font-medium text-primary" dir="rtl">
                        {hebrewDate.hebrewDateLabel}
                      </dd>
                    </div>
                  ) : null}
                  <div className="flex justify-between">
                    <dt className="text-irongrey/70">Sponsorship</dt>
                    <dd className="font-medium text-irongrey">
                      {sponsorshipLabel}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-irongrey/70">Amount</dt>
                    <dd className="font-medium text-irongrey">
                      {formatCurrencyCents(sponsorshipAmountCents)}
                    </dd>
                  </div>
                  {feeCents > 0 ? (
                    <div className="flex justify-between">
                      <dt className="text-irongrey/70">Processing Support</dt>
                      <dd className="font-medium text-irongrey">
                        {formatCurrencyCents(feeCents)}
                      </dd>
                    </div>
                  ) : null}
                </dl>

                <hr className="my-4 border-border" />
                <div className="flex justify-between text-base font-semibold text-irongrey">
                  <span>Total</span>
                  <span>{formatCurrencyCents(totalCents)}</span>
                </div>

                {formData.dedicationType ? (
                  <div className="mt-4 rounded-lg bg-amber-50 p-3">
                    <p className="text-xs font-medium text-amber-700">
                      {formData.dedicationType === "HONOR"
                        ? "In Honor Of"
                        : "In Memory Of"}
                      : {formData.honoreeName || "..."}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </form>
      </YstRouteTransition>
    </main>
  );
}

// ---------------------------------------------------------------------------
// 3. Success Page
// ---------------------------------------------------------------------------

function ParnesHayomSuccessPage() {
  const [searchParams] = useSearchParams();
  const { data: tenantConfig } = useGetDonationsTenantConfigQuery();
  const settingsQuery = useGetSettingsModuleConfigQuery({
    moduleKey: SETTINGS_MODULE_KEY,
  });
  const isSuccess = searchParams.get("checkout") === "success";
  const dateParam = searchParams.get("date");
  const selectedTier = resolveSponsorshipTier(searchParams.get("tier"));
  const selectedDivisionId = searchParams.get("divisionId") ?? "";

  const settingsState = useMemo(() => {
    const payload = asRecord(settingsQuery.data).payload;
    return parseSettingsState(payload);
  }, [settingsQuery.data]);
  const divisions = useMemo(
    () => resolveEnabledDivisionOptions(settingsState.divisions),
    [settingsState.divisions],
  );
  const selectedDivision =
    divisions.find((division) => division.id === selectedDivisionId) ?? null;
  const sponsorshipLabel = resolveSponsorshipLabel(
    selectedTier,
    selectedDivision?.displayName ?? null,
  );

  if (!isSuccess) {
    return <Navigate to="/parent/portal/parnes-hayom" replace />;
  }

  return (
    <main
      data-testid="parent-parnes-hayom-success"
      data-extracted-from="source:parent-parnes-hayom-v1.1"
      data-route-ready="true"
    >
      <YstRouteTransition className="mx-auto max-w-lg space-y-6 text-center">
        <PaymentReturnNotice
          state="success"
          noticeKey="parent-parnes-hayom-success"
          successTitle="Sponsorship submitted successfully"
          successBody="Stripe returned successfully. Your donation history and moderation state below reflect the next authoritative steps."
          cancelTitle=""
          cancelBody=""
          guidanceBody="Donation history is the authoritative record for completed sponsorships. If moderation is enabled for this tenant, the dedication can remain pending until review finishes."
        />
        <div className="flex justify-center">
          <div className="rounded-full bg-aquamarine/10 p-4">
            <CheckCircle2
              className="h-12 w-12 text-aquamarine"
              aria-hidden="true"
            />
          </div>
        </div>

        <div>
          <h1 className="yst-surface-title text-2xl">
            Thank You for Sponsoring a Day of Learning
          </h1>
          <p className="yst-surface-subtitle mt-2">
            Your generous sponsorship helps support Torah learning at our
            yeshiva.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-white p-4 text-left shadow-sm">
          <dl className="space-y-2 text-sm">
            {dateParam ? (
              <div className="flex items-center justify-between gap-4">
                <dt className="text-irongrey/60">Date</dt>
                <dd className="font-medium text-irongrey">{dateParam}</dd>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4">
              <dt className="text-irongrey/60">Sponsorship</dt>
              <dd className="font-medium text-irongrey">{sponsorshipLabel}</dd>
            </div>
          </dl>
        </div>

        {tenantConfig?.moderationDefault === "manual_review" ? (
          <div className="yst-banner-warning p-4">
            <div className="flex items-center justify-center gap-2 text-sm text-amber-700">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span className="font-medium">
                Dedication is pending moderation
              </span>
            </div>
            <p className="mt-1 text-xs text-amber-600/70">
              Your dedication will be reviewed and posted shortly.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-aquamarine/30 bg-aquamarine/10 p-4">
            <div className="flex items-center justify-center gap-2 text-sm text-jungle">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span className="font-medium">Dedication is active</span>
            </div>
            <p className="mt-1 text-xs text-jungle/70">
              Your sponsorship is available in the live honorees feed.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a
            href="/parent/portal/parnes-hayom/history"
            className="yst-button-secondary inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium"
          >
            <History className="h-4 w-4" aria-hidden="true" />
            View Donation History
          </a>
          <a
            href={buildParnesHayomRoute("/parent/portal/parnes-hayom", {
              tier: selectedTier,
              divisionId:
                selectedTier === "one_division"
                  ? (selectedDivision?.id ?? null)
                  : null,
            })}
            className="yst-button-primary inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold"
          >
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
            Sponsor Another Day
          </a>
        </div>
      </YstRouteTransition>
    </main>
  );
}

// ---------------------------------------------------------------------------
// 4. History Page
// ---------------------------------------------------------------------------

function ParnesHayomHistoryPage() {
  const {
    data: historyData,
    isLoading,
    error,
  } = useListDonationsFamilyHistoryQuery({
    limit: 25,
    campaignType: "parnes-hayom",
  });

  const items = historyData?.items ?? [];

  return (
    <main
      data-testid="parent-parnes-hayom-history"
      data-extracted-from="source:parent-parnes-hayom-v1.1"
      data-route-ready="true"
    >
      <YstRouteTransition className="space-y-6">
        <div>
          <p className="yst-surface-eyebrow">Parnes HaYom</p>
          <h1 className="yst-surface-title mt-1">Donation History</h1>
        </div>

        <ParnesHayomTabs activeTab="history" />

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : error ? (
          <SupportErrorMessage className="p-4">
            Donation history is temporarily unavailable.
          </SupportErrorMessage>
        ) : items.length === 0 ? (
          <div className="yst-surface-panel p-8 text-center">
            <CalendarDays
              className="mx-auto h-10 w-10 text-irongrey/30"
              aria-hidden="true"
            />
            <p className="mt-3 text-sm font-medium text-irongrey/60">
              No donations found
            </p>
            <p className="mt-1 text-xs text-irongrey/40">
              Your Parnes HaYom donations will appear here.
            </p>
            <a
              href="/parent/portal/parnes-hayom"
              className="yst-motion-color mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <Heart className="h-4 w-4" aria-hidden="true" />
              Sponsor a Day
            </a>
          </div>
        ) : (
          <div className="yst-surface-panel overflow-hidden">
            <div className="source-faithful-tableScroll">
              <table className="min-w-[920px] text-left text-sm" role="table">
                <thead className="yst-surface-table-head border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-medium text-irongrey/70">
                      Date
                    </th>
                    <th className="px-4 py-3 font-medium text-irongrey/70">
                      Hebrew Date
                    </th>
                    <th className="px-4 py-3 font-medium text-irongrey/70">
                      Sponsorship
                    </th>
                    <th className="hidden px-4 py-3 font-medium text-irongrey/70 md:table-cell">
                      Dedication
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-irongrey/70">
                      Amount
                    </th>
                    <th className="px-4 py-3 font-medium text-irongrey/70">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((item) => {
                    const sponsorshipLabel = resolveHistorySponsorshipLabel(
                      item as Record<string, unknown>,
                    );
                    return (
                      <tr
                        key={item.id}
                        className="yst-motion-color hover:bg-muted/70"
                      >
                        <td className="px-4 py-3 text-irongrey">
                          {item.targetGregorianDate}
                        </td>
                        <td className="px-4 py-3 text-primary" dir="rtl">
                          {item.hebrewDateLabel}
                        </td>
                        <td className="px-4 py-3 text-irongrey/70">
                          {sponsorshipLabel}
                        </td>
                        <td className="hidden px-4 py-3 text-irongrey/70 md:table-cell">
                          {item.dedicationType && item.honoreeName
                            ? `${item.dedicationType === "HONOR" ? "In Honor" : "In Memory"}: ${item.honoreeName}`
                            : "\u2014"}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-irongrey">
                          $
                          {(
                            (item.grossCents + item.feeRecoveryCents) /
                            100
                          ).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <DonationStatusBadge
                            status={item.status}
                            moderation={item.moderationStatus}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </YstRouteTransition>
    </main>
  );
}

function DonationStatusBadge({
  status,
  moderation,
}: {
  status: string;
  moderation: string;
}) {
  const label = moderation === "PENDING" ? "Pending Review" : status;
  const style =
    status === "PAID"
      ? "bg-[#28E2A7]/10 text-[#28E2A7]"
      : status === "PENDING"
        ? "bg-amber-50 text-amber-700"
        : status === "FAILED"
          ? "bg-red-50 text-red-700"
          : "bg-gray-100 text-[#404f4d]/60";

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${style}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Route Wrapper
// ---------------------------------------------------------------------------

export function ParentParnesHayomSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<ParnesHayomCampaignLandingPage />} />
      <Route path="checkout" element={<ParnesHayomCheckoutPage />} />
      <Route path="success" element={<ParnesHayomSuccessPage />} />
      <Route path="history" element={<ParnesHayomHistoryPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
